import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useAdaptiveAnimation } from '../../../hooks/useAdaptiveAnimation';
import { PremiumLoader } from './PremiumLoader';
import { cn } from '../../../lib/utils';

/**
 * RouteLoader component props interface
 * Defines the structure for route transition loading states
 */
export interface RouteLoaderProps {
  /** Whether the loader is currently active */
  isLoading?: boolean;
  /** Custom loading message */
  message?: string;
  /** Minimum display time in milliseconds (prevents flash) */
  minDisplayTime?: number;
  /** Custom CSS classes */
  className?: string;
}

/**
 * RouteLoader Component - Route Transition Loading State
 *
 * Provides smooth loading states during route transitions with
 * intelligent timing to prevent loading flashes and ensure UX consistency.
 *
 * Features:
 * - Prevents loading flash with minimum display time
 * - Smooth fade transitions
 * - Intelligent timing based on device capabilities
 * - Fullscreen overlay with backdrop blur
 * - Accessibility compliant
 * - Adaptive animation system integration
 *
 * @param props - RouteLoader component properties
 * @returns JSX.Element - Route transition loader component
 */
export const RouteLoader: React.FC<RouteLoaderProps> = ({
  isLoading = false,
  message = 'Loading page...',
  minDisplayTime = 300,
  className,
}) => {
  const { reducedMotion } = useAdaptiveAnimation();
  const [shouldShow, setShouldShow] = React.useState(false);
  const [startTime, setStartTime] = React.useState<number | null>(null);

  // Handle minimum display time to prevent loading flash
  React.useEffect(() => {
    if (isLoading && !startTime) {
      // Start loading
      setStartTime(Date.now());
      setShouldShow(true);
    } else if (!isLoading && startTime) {
      // End loading - check minimum display time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

      if (remainingTime > 0) {
        // Wait for minimum display time
        const timer = setTimeout(() => {
          setShouldShow(false);
          setStartTime(null);
        }, remainingTime);
        return (): void => clearTimeout(timer);
      } else {
        // Minimum time already passed
        setShouldShow(false);
        setStartTime(null);
      }
    }
  }, [isLoading, startTime, minDisplayTime]);

  // Animation variants for smooth transitions
  const containerVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: reducedMotion ? 0 : 0.3,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: reducedMotion ? 0 : 0.2,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1,
        duration: reducedMotion ? 0 : 0.4,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    },
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center',
            'bg-white/90 dark:bg-gray-900/90',
            'backdrop-blur-md',
            'border border-gray-200/20 dark:border-gray-700/20',
            className
          )}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="status"
          aria-live="polite"
          aria-label="Loading page content"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0 bg-linear-to-br from-emerald-500/10 via-transparent to-teal-500/10" />
            <div className="absolute inset-0 bg-linear-to-tl from-teal-500/10 via-transparent to-emerald-500/10" />
          </div>

          {/* Loading Content */}
          <motion.div
            className="relative z-10 flex flex-col items-center space-y-8"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Main Loader */}
            <PremiumLoader
              size="lg"
              variant="spinner"
              showMessage={false}
              className="drop-shadow-lg"
            />

            {/* Loading Message */}
            <motion.div
              className="text-center space-y-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <p className="font-jakarta font-semibold text-lg text-gray-900 dark:text-white">
                {message}
              </p>
              <p className="font-ui text-sm text-gray-600 dark:text-gray-300">
                Please wait while we prepare your content
              </p>
            </motion.div>

            {/* Progress Indicator */}
            <motion.div
              className="w-48 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <motion.div
                className="h-full bg-linear-to-r from-emerald-500 to-teal-500 rounded-full"
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                transition={{
                  duration: reducedMotion ? 0 : 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RouteLoader;
